import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api from '../utils/api';
import { lockBodyScroll } from '../utils/scrollLock';

// ─── 维护模式闸门 ────────────────────────────────────────────────────────────
// 维护开启时：非管理员（含未登录访客、代理、普通用户）从任何页面（包括登录页）进来，
// 看到的都是铺满整个屏幕的“系统升级中，请耐心等待…”，底层页面不渲染，
// 避免后台请求被 503 拒绝后弹出一串错误 toast。管理员不受影响（所以管理员自己开启后看不到全屏页，
// 想看效果用后台“我的”页里的“预览全屏”）。
//
// 状态来源（三路互补）：
//   1. GET /api/maintenance（公开接口）：进入网页时查询；之后轮询——
//      维护中 5 秒一次（升级结束 / 文案变化自动生效），未维护 30 秒一次（兜底：未登录访客没有 socket）
//   2. socket 事件 maintenance_updated（payload: { enabled, message }）：管理员开关 / 改文案后已登录用户即时生效
//   3. 任意 API 返回 503 MAINTENANCE（api.js 派发 window 事件）：连接兜底，并立刻重新查询拿最新文案
//
// 首屏不闪：第一次查询回来之前，非管理员先显示一块空白底（最多等 3 秒，查询失败也放行），
// 而不是先渲染出登录页再被维护页盖掉。
export default function MaintenanceGate({ children }) {
  const { user, loading } = useAuth();
  const { subscribe } = useSocket() || {};
  const [state, setState] = useState({ enabled: false, message: '', known: false });

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/maintenance');
      setState({ enabled: !!res.data.enabled, message: res.data.message || '', known: true });
    } catch {
      // 网络抖动：保持当前状态，不误判；首次就失败也不能把整站卡在空白页
      setState(s => (s.known ? s : { ...s, known: true }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // 查询一直没回来（网络很慢）：3 秒后放行，避免空白页卡死
  useEffect(() => {
    if (state.known) return undefined;
    const t = setTimeout(() => setState(s => (s.known ? s : { ...s, known: true })), 3000);
    return () => clearTimeout(t);
  }, [state.known]);

  useEffect(() => {
    const id = setInterval(refresh, state.enabled ? 5000 : 30000);
    return () => clearInterval(id);
  }, [state.enabled, refresh]);

  // 切回前台立即校准（手机息屏/切后台后回来）
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((evt, payload) => {
      if (evt === 'maintenance_updated') {
        setState({ enabled: !!payload?.enabled, message: payload?.message || '', known: true });
      }
    });
  }, [subscribe]);

  useEffect(() => {
    const on = () => { setState(s => ({ ...s, enabled: true, known: true })); refresh(); };
    window.addEventListener('maintenance:on', on);
    return () => window.removeEventListener('maintenance:on', on);
  }, [refresh]);

  const isAdmin = user?.role === 'admin';
  // 登录态还没从 localStorage 读出来时不拦截，避免管理员刷新页面瞬间闪一下提示
  const blocked = state.enabled && !loading && !isAdmin;
  if (blocked) return <MaintenanceScreen message={state.message} />;
  // 状态还没查到：先留白，别让登录页闪一下
  if (!state.known && !isAdmin) return <div className="maint-hold" style={{ minHeight: '100vh' }} />;
  return (
    <>
      {children}
      {state.enabled && isAdmin && <div className="maint-admin-pill">维护模式进行中 · 仅管理员可访问</div>}
    </>
  );
}

// 全屏维护页。preview=true 时是后台“预览全屏”：盖在当前页面上，左上角标记预览、右上角可退出，
// 不带管理员登录入口，Esc 也能退出。
export function MaintenanceScreen({ message = '', preview = false, onClose }) {
  const text = String(message || '').trim();

  useEffect(() => {
    if (!preview) return undefined;
    const unlock = lockBodyScroll();
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { unlock(); window.removeEventListener('keydown', onKey); };
  }, [preview, onClose]);

  const screen = (
    <div className="maint-screen" role="alert" aria-live="polite">
      {preview && (
        <>
          <span className="maint-preview-tag">预览 · 仅你可见</span>
          <button type="button" className="maint-preview-close" onClick={onClose}>退出预览</button>
        </>
      )}

      <div className="maint-body">
        <div className="maint-wrench" aria-hidden="true">
          <Wrench size={128} strokeWidth={1.3} className="maint-wrench-icon" />
          <span className="maint-shadow" />
        </div>
        <h1 className="maint-title">
          系统升级中，请耐心等待
          <span className="maint-dots" aria-hidden="true">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </h1>
        {/* 自定义文案按纯文本渲染（React 会转义），换行由样式 pre-wrap 保留 */}
        {text && <div className="maint-msg">{text}</div>}
        <p className="maint-sub">升级完成后将自动恢复访问</p>
      </div>

      {!preview && <AdminEntry />}
    </div>
  );
  return preview ? createPortal(screen, document.body) : screen;
}

// 管理员入口：默认只有一行极淡的小字，点开是账号密码表单；后端保证维护期间只有管理员能登录成功
function AdminEntry() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) { setError('请填写账号和密码'); return; }
    setSubmitting(true);
    setError('');
    try {
      const u = await login(identifier.trim(), password, password);
      navigate(u.role === 'admin' ? '/admin' : '/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || '登录失败，请检查网络');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="maint-footer">
      {!showLogin ? (
        <button type="button" className="maint-admin-link" onClick={() => setShowLogin(true)}>
          管理员登录
        </button>
      ) : (
        <form className="maint-login" onSubmit={handleSubmit}>
          <input
            className="maint-input"
            type="text"
            placeholder="管理员账号"
            autoComplete="username"
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
          />
          <input
            className="maint-input"
            type="password"
            placeholder="密码"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          {error && <div className="maint-error">{error}</div>}
          <div className="maint-login-actions">
            <button type="button" className="maint-btn maint-btn-ghost" onClick={() => { setShowLogin(false); setError(''); }}>
              取消
            </button>
            <button type="submit" className="maint-btn" disabled={submitting}>
              {submitting ? '登录中...' : '登录'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
